// Percorso file: src/utils/rigaGenitori.js
//
// LA RIGA «PARENT / PARENTS» della pagina Teams (cantiere TEAMS-LIST, E1,
// 25/9/2026) — il solo verso «tutori» della riga famiglia.
//
// ⚠️ CALCO DICHIARATO DI TEAM ASSIGNMENT. Questo modulo RICALCA, senza
// importarla, la regola della riga famiglia di `pages/TeamAssignment.jsx`
// (cantiere FAMIGLIA-LISTE, 9/9/2026, variante «b»), nel solo ramo dei
// TUTORI:
//   - `coorteDiStagione`  (TeamAssignment.jsx, la COORTE d'annata: tessera
//                          dell'anno in QUALUNQUE stato, `{ stati: null }`,
//                          oppure ADMIN/SUPERADMIN)       → `idDellaCoorte`
//   - `indiceDiPersona`   (l'indice per id su TUTTO `users`) → `indicePerId`
//   - `idInCoorte`        (chi si può nominare: solo la coorte guardata)
//   - `nomeDiPersona`     («Nome Cognome» oppure «Unnamed member»)
//   - `famigliaDiPersona` → `componi` e il ramo `tutori` (titolo «Parent» /
//                          «Parents», separatore « · », tutori SENZA squadra,
//                          id-fantasma col testo di View Profile alla lettera,
//                          ripiego da solo SENZA titolo) → `rigaGenitoriDiPersona`
// TeamAssignment.jsx NON si tocca (decisione D8 del committente): la regola
// vive quindi in DUE posti. ⚠️ CHI CAMBIA L'UNA DEVE GUARDARE L'ALTRA —
// testi, confine dell'annata, trattamento dei fantasmi.
//
// Differenze dichiarate rispetto a TA, e sole:
//   - si compone il solo verso dei TUTORI (`guardians` sul soggetto): i figli
//     non sono nella decisione D6 e non si calcolano;
//   - la riga è SEMPRE calcolata (nessun interruttore «Show family links»,
//     decisione D7).
//
// ⚠️ MODULO PURO: nessun React. Solo letture, nessuna chiamata.

import { hasRole } from "./roleUtils";
import { tessereDelSoggetto } from "./squadre";

/** L'indice per id su TUTTO l'elenco (calco di `indiceDiPersona`).
 *  ⚠️ Si indicizza l'elenco integrale, non la coorte: un id di `guardians`
 *  che non si risolve qui è un fantasma, non uno fuori annata. */
export const indicePerId = (users) => {
  const perId = new Map();

  (Array.isArray(users) ? users : []).forEach((u) => {
    const id = String((u && u.id) || "");
    if (id) perId.set(id, u);
  });

  return perId;
};

/** Gli id della COORTE d'annata (calco di `coorteDiStagione` + `idInCoorte`):
 *  chi ha almeno una tessera dell'anno in qualunque stato, oppure è
 *  ADMIN/SUPERADMIN (gate unico, `hasRole`). */
export const idDellaCoorte = (users, anno) =>
  new Set(
    (Array.isArray(users) ? users : [])
      .filter(
        (u) =>
          tessereDelSoggetto(u?.contexts, anno, { stati: null }).length > 0 ||
          hasRole(u, "ADMIN", "SUPERADMIN")
      )
      .map((u) => String((u && u.id) || ""))
  );

/** Il nome a schermo di un collegato (calco di `nomeDiPersona`). */
const nomeDiPersona = (p) => {
  const nome = [p && p.firstName, p && p.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return nome || "Unnamed member";
};

/**
 * La riga dei tutori di una persona, pronta da scrivere sotto il nome, oppure
 * `null` quando non c'è niente da dire (calco di `famigliaDiPersona` →
 * `componi`, ramo `tutori`).
 *
 * @param {object} u              la persona
 * @param {Map} indiceDiPersona   da `indicePerId`
 * @param {Set} idInCoorte        da `idDellaCoorte`
 * @returns {string|null}
 */
export const rigaGenitoriDiPersona = (u, indiceDiPersona, idInCoorte) => {
  const idPersona = String((u && u.id) || "");

  // I TUTORI, dal campo del soggetto (faccia autorevole). La Map toglie i
  // doppioni; l'auto-riferimento si scarta.
  const tutori = new Map();

  (Array.isArray(u && u.guardians) ? u.guardians : []).forEach((id) => {
    const chiave = String(id || "");
    if (!chiave || chiave === idPersona || tutori.has(chiave)) return;
    tutori.set(chiave, indiceDiPersona.get(chiave) || null);
  });

  const nominati = [];
  let fantasmi = 0;

  tutori.forEach((persona) => {
    if (!persona) {
      fantasmi += 1;
      return;
    }
    if (!idInCoorte.has(String(persona.id || ""))) return;

    nominati.push(nomeDiPersona(persona));
  });

  const voci = nominati.slice();
  if (fantasmi === 1) voci.push("Guardian on file — profile not available");
  else if (fantasmi > 1) {
    voci.push(`${fantasmi} guardians on file — profiles not available`);
  }

  if (!voci.length) return null;

  // Il ripiego da solo non vuole titolo (come in TA).
  if (!nominati.length) return voci.join(" · ");

  return `${voci.length > 1 ? "Parents" : "Parent"}: ${voci.join(" · ")}`;
};
